/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Fragment, useContext, useMemo } from 'react';
import { useIsFetching } from 'react-query';
import { Spin, Dropdown, Menu } from 'antd';
import { useIntl } from 'react-intl';

import { useStyles, ThemedStyleFn } from '~src/hooks/useStyles';
import { useCurrentFolder } from '~src/hooks/useCurrentFolder';
import { useProjectId } from '~src/hooks/useProjectId';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useHistory } from 'react-router-dom';
import SidebarContext from '~src/contexts/SidebarContext';
import { useThemeSwitcher } from 'react-css-theme-switcher';
import AppContext from '~src/contexts/AppContext';
import { useIsOnStart } from '~src/hooks/useIsOnStart';
import dataContextMenu from '~src/data/dataContextMenu';
import { useIsUserAuthenticated } from '~src/hooks/api-hooks/useUserSettings';
import { useIsPath } from '~src/hooks/useIsPath';
import { useProjectList } from '~src/hooks/api-hooks/useProjectList';

const LeftSideNav = () => {
  const style = useStyles(styleFn);
  const intl = useIntl();

  const currentFolder = useCurrentFolder();
  const projectId = useProjectId();
  const history = useHistory();
  const isAuthenticated = useIsUserAuthenticated();

  const { isSidebarMinimized, setIsSidebarMinimized } = useContext(
    SidebarContext
  );
  const { setAppContextState } = useContext(AppContext);
  const { switcher, themes } = useThemeSwitcher();

  const isGlobalFetching = useIsFetching();

  const toggleSidebar = () => {
    setIsSidebarMinimized((prev) => !prev);
  };

  const isProjectDetailsPage = useIsPath('/projects');

  const projectListQuery = useProjectList({
    // onError: () => history.replace(`/projects/${projectId}`),
    refetchOnMount: false,
  });

  const hasProjects = (projectListQuery.data?.length ?? 0) > 0;

  const onBackToProjects = () => {
    switcher({ theme: themes.light });
    window.sessionStorage.setItem('appState', 'normal');
    setAppContextState('normal');
    history.push({
      pathname: `/my-projects`,
    });
  };

  const onGoToDashboard = () => {
    switcher({ theme: themes.light });
    window.sessionStorage.setItem('appState', 'normal');
    setAppContextState('normal');
    history.push('/');
  };

  const onBackOneLevel = () => {
    history.goBack();
    // const parentId = (currentFolder as any)?.parent_id ?? (currentFolder as any)?.parent_folder_id;
    // if (parentId) history.push(`/project/${projectId}/folders/${parentId}`);
    // else onBackToProjects();
  };

  const handleHamburgerAction = (modalKey: string) => {
    switch (modalKey) {
      case 'leftnav-toggle':
        toggleSidebar();
        break;
      case 'leftnav-back-projects':
        onBackToProjects();
        break;
      case 'leftnav-back-one':
        onBackOneLevel();
        break;
      case 'leftnav-dashboard':
        onGoToDashboard();
        break;
      default:
        break;
    }
  };

  const items = useMemo(() => {
    const baseItems = isSidebarMinimized
      ? dataContextMenu.leftNavHamburgerMenuCollapsed
      : dataContextMenu.leftNavHamburgerMenuExpanded;

    // Hide "back to projects list" for:
    // - unauthenticated users
    // - not being on /projects pages
    // - users with 0 projects
    const shouldHideBackToProjects =
      !isAuthenticated || !isProjectDetailsPage || !hasProjects;

    if (shouldHideBackToProjects) {
      return baseItems.filter((it) => it.name !== 'back-to-projects-list');
    }

    return baseItems;
  }, [isSidebarMinimized, isAuthenticated, isProjectDetailsPage, hasProjects]);

  const hamburgerMenu = useMemo(
    () => (
      <Menu onClick={({ key }) => handleHamburgerAction(String(key))}>
        {items.map((it) => {
          return (
            <Menu.Item
              key={it.modal as string}
              icon={
                <FontAwesomeIcon icon={[it.iconStyle as any, it.icon as any]} />
              }
              disabled={(it as any).disabled}
              danger={(it as any).danger}>
              {intl.formatMessage({
                id: it.name,
                defaultMessage: it.name,
              })}
            </Menu.Item>
          );
        })}
      </Menu>
    ),
    [items, intl]
  );

  return (
    <Fragment>
      <div css={style.component}>
        {isGlobalFetching > 0 && <Spin size="small" />}

        {!useIsOnStart && (
          <FontAwesomeIcon
            style={{ marginLeft: isGlobalFetching ? '10px' : '0px' }}
            cursor="pointer"
            icon={['fas', 'arrow-left']}
            onClick={onBackToProjects}
          />
        )}

        {window.sessionStorage.getItem('appState') !== 'superUser' && (
          <Dropdown overlay={hamburgerMenu} trigger={['click']}>
            <span>
              <FontAwesomeIcon
                style={{ marginLeft: '10px' }}
                cursor="pointer"
                icon={['fas', 'bars']}
              />
            </span>
          </Dropdown>
        )}
      </div>
    </Fragment>
  );
};

export default LeftSideNav;

const styleFn: ThemedStyleFn = (theme) => ({
  component: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor:
      window.sessionStorage.getItem('appState') === 'superUser'
        ? theme.colors.primaryLight
        : window.sessionStorage.getItem('appState') === 'timeMachine'
        ? theme.colors.background.primaryLight
        : theme.colors.white,
    margin: '0 5px',
    paddingLeft: '6px',
    height: '44px',
    minHeight: '44px',
    borderBottom: `1px solid ${theme.colors.darkGray}`,
  },
  headline: {
    color: theme.colors.text.primary,
    fontWeight: 'bold',
  },
  icons: {
    color: theme.colors.secondary,
    fontSize: '20px',
    margin: '0 2px',
  },
});
